import type { RouterStateSnapshot } from '@angular/router';
import type { SiteContext } from '../app/core/site-context/site-context';
import type { Brand } from '../app/core/brand/brand-palette.model';

/**
 * Mutable per-request capture populated by AppServerModule during SSR.
 * Bundled into the request context passed to `AngularNodeAppEngine.handle()`
 * so both Express (which owns the object) and Angular (via `REQUEST_CONTEXT`)
 * see it by reference. After `handle()` resolves, Express reads
 * `routerSnapshot` to emit cache headers based on the matched route's metadata.
 *
 * Why this isn't stored in AsyncLocalStorage: ALS context does not survive
 * across Angular's Zone.js router event callbacks, so the value written
 * inside Angular is never visible to the Express caller. Passing a shared
 * object by reference through `REQUEST_CONTEXT` sidesteps the problem.
 */
export interface RenderCapture {
  routerSnapshot: RouterStateSnapshot | null;
  cacheTags: string[];
  /**
   * Set true when the matched navigation's final URL is `/404` — i.e. a guard (e.g.
   * `contentPageExistsGuard`) redirected there rather than the request having been routed
   * there directly. `server.ts` reads this to rewrite the response status to 404, the same way
   * it does for `siteContext.app === 'invalid-host'` (spec §4: an unknown page must 404, not
   * serve a 200 at the end of a redirect — final-review F9).
   */
  notFound: boolean;
}

/**
 * The object passed as the second argument to `AngularNodeAppEngine.handle()`.
 * Angular components / services see this via `inject(REQUEST_CONTEXT)` —
 *
 *  - `AppModule` reads `siteContext` to provide the `SITE_CONTEXT` token.
 *  - `AppModule` reads `brand` to provide the `BRAND` token.
 *  - `AppServerModule` reads `capture` and writes the router snapshot into it
 *    on the first `NavigationEnd`.
 *
 * After `handle()` resolves, `server.ts` reads `capture.routerSnapshot` back
 * off the same object and feeds it to `emitCacheHeaders`.
 */
export interface ServerRequestContext {
  siteContext: SiteContext;
  brand: Brand;
  capture: RenderCapture;
}

/**
 * Narrow an `unknown` value from `REQUEST_CONTEXT` to `ServerRequestContext`.
 * Returns `null` if the value isn't shaped correctly (e.g. during browser
 * bootstrap where `REQUEST_CONTEXT` is absent).
 */
export function asServerRequestContext(value: unknown): ServerRequestContext | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<ServerRequestContext>;
  if (!candidate.siteContext || !candidate.brand || !candidate.capture) return null;
  return candidate as ServerRequestContext;
}

/** Create an empty capture for a new request. */
export function createRenderCapture(): RenderCapture {
  return { routerSnapshot: null, cacheTags: [], notFound: false };
}
